import vcRecaptcha from 'angular-recaptcha';

import { MobileAnalyticsServiceModule } from 'services/tracking/mobileApps/mobileAnalytics.service';
import { CreditCardServiceModule } from 'services/payment/stripe/creditCard.service';
import { CheckoutServiceModule } from 'components/checkout/checkout.service';
import { SubscriptionServiceModule } from 'services/access/subscription/subscription.service';
import toNumber from 'lodash/toNumber';

import { STRIPE_ELEMENTS_APPLE_PAY, STRIPE_ELEMENTS_GOOGLE_PAY } from 'app/constants';

import template from './orderSummary.html';
import './orderSummary.scss';

export const CheckoutOrderSummaryModule =
    angular.module('app.components.checkout.components.checkoutOrderSummary', [
        CheckoutServiceModule,
        SubscriptionServiceModule,
        MobileAnalyticsServiceModule,
        CreditCardServiceModule,
        'ngMaterial',
        vcRecaptcha
    ]).component('checkoutOrderSummary', {
        template,
        bindings: {
            canPlaceOrder: '<',
            cart: '<',
            coupon: '<',
            expireTrialInFiveMinutes: '<',
            onPurchaseComplete: '&',
            trialDaysRemaining: '<',
            user: '<',
            selectedPaymentMethod: '<',
            requireRecaptcha: '<'
        },
        controller: class CheckoutOrderSummaryCtrl {
            constructor(
                checkoutService,
                subscriptionsService,
                $mdDialog,
                $translate,
                creditCardUtilities,
                mobileAnalyticsService,
                creditCardService,
                vcRecaptchaService) {
                'ngInject';

                angular.extend(this, {
                    checkoutService,
                    subscriptionsService,
                    $mdDialog,
                    $translate,
                    creditCardUtilities,
                    mobileAnalyticsService,
                    creditCardService,
                    vcRecaptchaService,
                    LEGAL_TERMS_URL: process.env.LEGAL_TERMS_URL,
                    PRIVACY_POLICY_URL: process.env.PRIVACY_POLICY_URL,
                    recaptchaSiteKey: process.env.RECAPTCHA_SITE_KEY
                });
            }

            $onInit() {
                this.$translate([
                    'REGISTER.REGISTRATION_ERROR_TITLE',
                    'REGISTER.REGISTRATION_ERROR_DESCRIPTION',
                    'REGISTER.PROCESSINGSTEP1',
                    'CHANGEPLAN.ERROR_TITLE',
                    'CHANGEPLAN.ERROR_DESCRIPTION'
                ]).then((translations) => {
                    this.translations = translations;
                });

                this.taxAmount = 0;
                this.onPaymentRequestTokenCreated = this.onPaymentRequestTokenCreated.bind(this);
                this.onShippingAddressChange = this.onShippingAddressChange.bind(this);
                let paymentRequest = this.buildPaymentRequest();
                this.setupElementPaymentRequest(paymentRequest);
                this.grandTotal = this.getGrandTotal();
            }

            buildPaymentRequest() {
                this.stripe = this.creditCardService.getStripe();
                return this.stripe.paymentRequest(this.buildPaymentRequestOptions(this.cart.planDetails));
            }

            buildPaymentRequestOptions(plan) {
                return {
                    country: 'US',
                    currency: 'usd',
                    total: {
                        label: `${plan.name}`,
                        amount: plan.amount
                    },
                    requestPayerEmail: true,
                    requestShipping: true,
                    shippingOptions: [
                        {
                            id: 'digital-subscription',
                            label: 'Digital Subscription',
                            amount: 0
                        }
                    ]
                };
            }

            setupElementPaymentRequest(paymentRequest) {
                let elements = this.stripe.elements();
                let prButton = elements.create('paymentRequestButton', {
                    paymentRequest: paymentRequest,
                    style: {
                        paymentRequestButton: {
                            type: 'default'
                        }
                    }
                });
                paymentRequest.canMakePayment()
                    .then((result) => {
                        if (result) {
                            prButton.mount('#payment-request-button');
                        }
                    });
                paymentRequest.on('shippingaddresschange', this.onShippingAddressChange);
                paymentRequest.on('token', this.onPaymentRequestTokenCreated);
            }

            onShippingAddressChange(event) {
                this.checkoutService.logCheckoutStep(this.checkoutService.STEP_SHIPPINGADDRESS,
                    this.cart.planDetails,
                    this.selectedPaymentMethod);
                return this.subscriptionsService.getGrandTotal(this.cart.planDetails, event.shippingAddress.region,
                    event.shippingAddress.postalCode)
                    .then((grandTotalData) => {
                        this.grandTotal = grandTotalData.grandTotalFormatted;
                        this.taxAmount = grandTotalData.taxAmountFormatted;
                        event.updateWith({
                            status: 'success',
                            total: {
                                amount: grandTotalData.grandTotal,
                                label: this.cart.planDetails.name
                            }
                        });
                    });
            }

            onPaymentRequestTokenCreated(event) {
                this.checkoutService.logCheckoutStep(this.checkoutService.STEP_CONTACTINFO,
                    this.cart.planDetails,
                    this.selectedPaymentMethod);
                this.checkoutService.logCheckoutStep(this.checkoutService.STEP_PAYMENTMETHOD,
                    this.cart.planDetails,
                    this.selectedPaymentMethod);
                return this.subscriptionsService.changePlan({
                    planId: this.cart.planDetails.id,
                    stripeToken: event.token.id,
                    couponCode: '',
                    trialDaysRemaining: this.trialDaysRemaining,
                    address: {
                        state: event.shippingAddress.region,
                        zip: event.shippingAddress.postalCode
                    }
                }).then((subscription) => {
                    const subscriptionInfo = {
                        id: subscription.id,
                        tenantId: subscription.tenant,
                        customerId: subscription.customer
                    };
                    this.cart.couponCode = this.coupon;
                    this.checkoutService.logPurchase(this.cart,
                        subscriptionInfo,
                        this.user,
                        this.selectedPaymentMethod);
                    event.complete('success');
                    this.purchaseComplete = true;
                    this.onPurchaseComplete();
                });
            }

            onRecaptchaSuccess(response) {
                this.placeOrder(response);
            }

            onPlaceOrderClicked() {
                if (this.requireRecaptcha) {
                    this.vcRecaptchaService.execute();
                } else {
                    this.placeOrder();
                }
            }

            placeOrder(recaptchaResponse) {
                this.checkoutService.logCheckoutStep(this.checkoutService.STEP_CONTACTINFO, this.cart.planDetails);
                this.checkoutService.logCheckoutStep(this.checkoutService.STEP_SHIPPINGADDRESS, this.cart.planDetails);
                this.loading = true;
                this.checkoutService.placeOrder(
                    this.user,
                    this.cart,
                    this.expireTrialInFiveMinutes,
                    this.trialDaysRemaining,
                    recaptchaResponse
                ).then(() => {
                    this.purchaseComplete = true;
                    this.onPurchaseComplete();
                    this.loading = false;
                }).catch((error) => {
                    let errorTitle = this.user.isLoggedIn ?
                        this.translations['CHANGEPLAN.ERROR_TITLE'] :
                        this.translations['REGISTER.REGISTRATION_ERROR_TITLE'];

                    let errorDescription;
                    if (error && error.message) {
                        errorDescription = error.message;
                    } else if (this.user.isLoggedIn) {
                        errorDescription = this.translations['CHANGEPLAN.ERROR_DESCRIPTION'];
                    } else {
                        errorDescription = this.translations['REGISTER.REGISTRATION_ERROR_DESCRIPTION'];
                    }

                    this.mobileAnalyticsService.trackEvent('Order:Failed', {});
                    if (this.requireRecaptcha) {
                        this.vcRecaptchaService.reload();
                    }
                    this.loading = false;
                    this.$mdDialog.show(
                        this.$mdDialog.alert()
                            .clickOutsideToClose(true)
                            .title(errorTitle)
                            .textContent(errorDescription)
                            .ariaLabel(errorTitle)
                            .ok('Ok')
                            .multiple(true));
                });
            }

            calculateSubtotal() {
                const planAmount = toNumber(this.cart.planDetails.amount) / 100;
                const subtotal = planAmount - this.getDollarsOff(this.coupon);
                return subtotal > 0 ? subtotal : 0;
            }

            getGrandTotal() {
                const subtotal = this.calculateSubtotal();
                return subtotal + this.taxAmount;
            }

            getGrandTotalPerInterval() {
                return this.grandTotal / this.cart.planDetails.interval_count;
            }

            getDollarsOff(coupon) {
                let discountAmount;
                if (coupon) {
                    const planAmount = this.getPlanAmountInDollars(this.cart.planDetails.amount);
                    discountAmount = coupon.percent_off ?
                        (planAmount * (toNumber(coupon.percent_off) / 100)) : this.getAmountOffInDollars(coupon);
                } else {
                    discountAmount = 0;
                }
                return discountAmount;
            }

            getPlanAmountInDollars(planAmount) {
                return toNumber(planAmount) / 100;
            }

            getAmountOffInDollars(coupon) {
                return toNumber(coupon.amount_off / 100);
            }

            getDiscountLabel(coupon) {
                let labelDiscountAmount = coupon.percent_off ?
                    `${coupon.percent_off}%` : `$${this.getAmountOffInDollars(coupon)}`;
                let labelDiscountDuration = coupon.duration === 'repeating' ?
                    `for ${coupon.duration_in_months} months` : coupon.duration;
                return `${labelDiscountAmount} off ${labelDiscountDuration}`;
            }

            shouldShowStripePaymentRequestButton() {
                return this.termsAndConditionsAccepted &&
                    (this.selectedPaymentMethod === STRIPE_ELEMENTS_GOOGLE_PAY ||
                        this.selectedPaymentMethod === STRIPE_ELEMENTS_APPLE_PAY);
            }
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/components/checkout/components/orderSummary/orderSummary.js